import { Check, Close, KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material';
import { Box, Button, Checkbox, Stack, Typography } from '@mui/material';
import { InputSearch, Scrollbar } from 'app/components';
import { useAsyncAction, usePermission } from 'app/services';
import { userGeofenceDelete, userGeofenceListDelete, userGeofenceListPush, userGeofencePush } from 'app/store/user';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
const UserGeofenceList = ({ searchValue, onSearchChange }) => {
    var _a;
    const editPermission = usePermission(['create', 'update', 'delete']);
    const { selectedItem } = useSelector((state) => state.user);
    const geofenceGroups = (_a = selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.geofenceGroups) !== null && _a !== void 0 ? _a : [];
    const [expandedId, setExpandedId] = useState(null);
    const { asyncAction: pushSubmit, isLoading: isPushSubmit } = useAsyncAction(userGeofencePush, [selectedItem]);
    const { asyncAction: deleteSubmit, isLoading: isDeleteSubmit } = useAsyncAction(userGeofenceDelete, [selectedItem]);
    const { asyncAction: addList, isLoading: isAddList } = useAsyncAction(userGeofenceListPush, [selectedItem]);
    const { asyncAction: deleteList, isLoading: isDeleteList } = useAsyncAction(userGeofenceListDelete, [selectedItem]);
    const isPending = isPushSubmit || isDeleteSubmit || isAddList || isDeleteList;
    useEffect(() => {
        setExpandedId(null);
    }, [selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id]);
    const items = useMemo(() => {
        const search = searchValue.trim().toLowerCase();
        if (!search) {
            return geofenceGroups;
        }
        return geofenceGroups
            .map((group) => {
            if (group.name.toLowerCase().includes(search)) {
                return group;
            }
            return Object.assign(Object.assign({}, group), { geofences: group.geofences.filter((geofence) => geofence.name.toLowerCase().includes(search)) });
        })
            .filter((group) => group.name.toLowerCase().includes(search) || group.geofences.length > 0);
    }, [geofenceGroups, searchValue]);
    const allGeofenceIds = useMemo(() => geofenceGroups.reduce((ids, group) => ids.concat(group.geofences.map((geofence) => geofence.id)), []), [geofenceGroups]);
    const checkedAllLength = geofenceGroups.reduce((accumulator, group) => accumulator + group.geofences.filter((geofence) => geofence.checked).length, 0);
    const isCheckedAll = allGeofenceIds.length !== 0 && checkedAllLength === allGeofenceIds.length;
    const isSearching = searchValue.trim().length > 0;
    const handleGeofenceClick = (geofence) => {
        if (!editPermission || isPending || !selectedItem)
            return;
        geofence.checked ? deleteSubmit(selectedItem.id, geofence.id) : pushSubmit(selectedItem.id, geofence.id);
    };
    const handleGroupClick = (group) => {
        if (!editPermission || isPending || !selectedItem || group.geofences.length === 0)
            return;
        const unchecked = group.geofences.filter((geofence) => !geofence.checked).map((geofence) => geofence.id);
        if (unchecked.length) {
            addList(selectedItem.id, unchecked);
        }
        else {
            deleteList(selectedItem.id, group.geofences.map((geofence) => geofence.id));
        }
    };
    const handleCheckAll = () => {
        if (!editPermission || isPending || !selectedItem)
            return;
        const uncheckedIds = geofenceGroups.reduce((ids, group) => {
            group.geofences.forEach((geofence) => {
                if (!geofence.checked)
                    ids.push(geofence.id);
            });
            return ids;
        }, []);
        addList(selectedItem.id, uncheckedIds);
    };
    const handleUncheckAll = () => {
        if (!editPermission || isPending || !selectedItem)
            return;
        deleteList(selectedItem.id, []);
    };
    const toggleGroup = (group) => {
        if (!group.geofences.length || isSearching)
            return;
        setExpandedId((current) => (current === group.id ? null : group.id));
    };
    const isGroupExpanded = (group) => {
        if (!group.geofences.length)
            return false;
        if (isSearching)
            return true;
        return expandedId === group.id;
    };
    return (React.createElement(Box, { sx: {
            width: '100%',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            '.simplebar-offset > div': {
                '&::-webkit-scrollbar': {
                    width: 0,
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                    boxShadow: 'none',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            },
        } },
        React.createElement(Stack, { direction: 'row', alignItems: 'center', spacing: 1.5, sx: { flexShrink: 0, padding: 2 } },
            React.createElement(Box, { sx: { flex: '1 1 auto', minWidth: 0, height: 36 } },
                React.createElement(InputSearch, { placeholder: '\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0433\u0440\u0443\u043F\u043F\u044B \u0438\u043B\u0438 \u0433\u0435\u043E\u0437\u043E\u043D\u044B', value: searchValue, onChange: onSearchChange })),
            editPermission && (React.createElement(React.Fragment, null,
                React.createElement(Button, { variant: 'contained', disableElevation: true, disabled: isPending || isCheckedAll || allGeofenceIds.length === 0, startIcon: React.createElement(Check, { sx: { fontSize: 18 } }), onClick: handleCheckAll, sx: actionButtonSx }, "\u0412\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0432\u0441\u0435"),
                React.createElement(Button, { variant: 'outlined', color: 'inherit', disableElevation: true, disabled: isPending || checkedAllLength === 0, startIcon: React.createElement(Close, { sx: { fontSize: 18 } }), onClick: handleUncheckAll, sx: Object.assign(Object.assign({}, actionButtonSx), { color: 'text.primary', borderColor: 'divider', bgcolor: 'background.paper', '&:hover': {
                            borderColor: 'divider',
                            bgcolor: 'action.hover',
                        } }) }, "\u041E\u0442\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0432\u0441\u0435")))),
        React.createElement(Box, { sx: { flex: '1 1 auto', minHeight: 0, borderTop: '1px solid', borderColor: 'divider' } },
            React.createElement(Scrollbar, null, items.map((group) => {
                const checkedCount = group.geofences.filter((geofence) => geofence.checked).length;
                const expanded = isGroupExpanded(group);
                return (React.createElement(Box, { key: group.id },
                    React.createElement(Box, { onClick: () => toggleGroup(group), sx: (theme) => (Object.assign(Object.assign({}, rowSx), { cursor: group.geofences.length ? 'pointer' : 'default', '&:hover': {
                                bgcolor: theme.palette.action.hover,
                            } })) },
                        React.createElement(Box, { sx: {
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                                flexShrink: 0,
                                color: 'text.secondary',
                            } }, expanded ? React.createElement(KeyboardArrowDown, { sx: { fontSize: 20 } }) : React.createElement(KeyboardArrowRight, { sx: { fontSize: 20 } })),
                        React.createElement(Checkbox, { size: 'small', color: 'primary', disableRipple: true, disabled: !editPermission || isPending, checked: group.geofences.length !== 0 && checkedCount === group.geofences.length, indeterminate: checkedCount !== 0 && checkedCount < group.geofences.length, onClick: (event) => handleCheckboxClick(event, () => handleGroupClick(group)), sx: checkboxSx }),
                        React.createElement(Typography, { noWrap: true },
                            group.name,
                            " (",
                            group.geofences.length,
                            ")")),
                    expanded &&
                        group.geofences.map((geofence) => (React.createElement(Box, { key: geofence.id, onClick: () => handleGeofenceClick(geofence), sx: (theme) => (Object.assign(Object.assign({}, rowSx), { paddingLeft: '52px', cursor: editPermission && !isPending ? 'pointer' : 'default', '&:hover': {
                                    bgcolor: theme.palette.action.hover,
                                } })) },
                            React.createElement(Checkbox, { size: 'small', color: 'primary', disableRipple: true, disabled: !editPermission || isPending, checked: !!geofence.checked, sx: checkboxSx }),
                            React.createElement(Typography, { noWrap: true }, geofence.name))))));
            })))));
};
const actionButtonSx = {
    flexShrink: 0,
    height: 36,
    px: 1.5,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
    whiteSpace: 'nowrap',
};
const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    minHeight: 44,
    px: 2,
    borderBottom: '1px solid',
    borderColor: 'divider',
    userSelect: 'none',
};
const checkboxSx = {
    p: '4px',
    flexShrink: 0,
};
const handleCheckboxClick = (event, action) => {
    event.preventDefault();
    event.stopPropagation();
    action();
};
export default UserGeofenceList;
